import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { ReportProblem, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { catService } from '../../../services/catService';
import type { Cat, PontoDoDiaCat } from '../../../types/cat';
import {
  CatStatus,
  CAT_STATUS_LABELS,
  CAT_STATUS_COLORS,
  ACCIDENT_TYPE_LABELS,
  formatCatDate,
} from '../../../types/cat';

type SituacaoDoPonto = 'sem-integracao' | 'sem-marcacao' | 'dentro-da-jornada' | 'fora-do-horario';

/** Compara `accidentTime` com a primeira e a última marcação do dia. Sem hora informada na CAT
 * ou sem nenhuma marcação, não dá pra concluir nada — fica "sem-marcacao". */
function situacaoDoPonto(ponto: PontoDoDiaCat | undefined): SituacaoDoPonto | null {
  if (!ponto) return null;
  if (ponto.marcacoes.length === 0) return 'sem-marcacao';
  if (!ponto.accidentTime) return 'dentro-da-jornada';

  const [horaAcidente, minutoAcidente] = ponto.accidentTime.split(':').map(Number);
  const minutosDoAcidente = horaAcidente * 60 + minutoAcidente;

  const horarios = ponto.marcacoes.map(m => {
    const d = new Date(m.punchAt);
    return d.getUTCHours() * 60 + d.getUTCMinutes();
  });
  const inicio = Math.min(...horarios);
  const fim = Math.max(...horarios);

  return minutosDoAcidente >= inicio && minutosDoAcidente <= fim ? 'dentro-da-jornada' : 'fora-do-horario';
}

const SITUACAO_LABELS: Record<SituacaoDoPonto, string> = {
  'sem-integracao': 'Sem ponto',
  'sem-marcacao': 'Sem marcação de ponto no dia',
  'dentro-da-jornada': 'Dentro da jornada',
  'fora-do-horario': 'Fora do horário batido',
};

const SITUACAO_COLORS: Record<SituacaoDoPonto, 'default' | 'warning' | 'success' | 'error'> = {
  'sem-integracao': 'default',
  'sem-marcacao': 'warning',
  'dentro-da-jornada': 'success',
  'fora-do-horario': 'error',
};

interface EmployeeCardProps {
  employeeId: number;
  companyId: number;
}

const ACCENT = '#B71C1C';
const PAGE_SIZE = 5;

export const EmployeeCatsCard: React.FC<EmployeeCardProps> = ({ employeeId }) => {
  const [page, setPage] = useState(0);
  const [pontoPorCat, setPontoPorCat] = useState<Record<number, PontoDoDiaCat>>({});

  // A CAT é do módulo de Segurança e tem permissão própria: o cartão vinha vazio e mudo
  // para quem só lê funcionário.
  const carregar = useCallback(
    () => catService.getByEmployee(employeeId).then((res) => (Array.isArray(res) ? res : res.data ?? [])),
    [employeeId]
  );
  const lista = useListaPermitida<Cat>('cat.read', 'CAT emitidas', carregar);
  const cats = lista.itens;
  const loading = lista.carregando;

  const statusCounts = cats.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  const totalPages = Math.ceil(cats.length / PAGE_SIZE);
  const paged = cats.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Cruza cada CAT da página atual com a marcação de ponto do dia do acidente — só busca o que
  // ainda não veio, pra não repetir a chamada ao trocar de página e voltar.
  useEffect(() => {
    const pendentes = paged.filter(c => !(c.id in pontoPorCat));
    if (pendentes.length === 0) return;

    pendentes.forEach(cat => {
      catService.getPontoDoDia(cat.id)
        .then(ponto => setPontoPorCat(prev => ({ ...prev, [cat.id]: ponto })))
        .catch(() => { /* sem integração de ponto ou sem permissão: chip simplesmente não aparece */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged.map(c => c.id).join(',')]);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><ReportProblem fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            CAT Emitidas
          </Typography>
          {!loading && !lista.erro && (
            <Chip label={cats.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {lista.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{lista.erro}</Alert>
        )}

        {!loading && !lista.erro && cats.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {lista.bloqueio ?? 'Nenhum registro encontrado'}
          </Typography>
        )}

        {!loading && !lista.erro && cats.length > 0 && (
          <>
            {/* Summary chips */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              <Chip label={`${cats.length} CAT${cats.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
              {Object.entries(statusCounts).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${CAT_STATUS_LABELS[status as CatStatus] || status}: ${count}`}
                  size="small"
                  color={CAT_STATUS_COLORS[status as CatStatus] || 'default'}
                  variant="outlined"
                />
              ))}
            </Box>

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((cat) => {
                const situacao = situacaoDoPonto(pontoPorCat[cat.id]);
                return (
                  <Box
                    key={cat.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <Typography variant="caption" sx={{ minWidth: 70 }}>
                      {formatCatDate(cat.accidentDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap title={ACCIDENT_TYPE_LABELS[cat.accidentType] || cat.accidentType}>
                      {ACCIDENT_TYPE_LABELS[cat.accidentType] || cat.accidentType}
                    </Typography>
                    {situacao && (
                      <Chip
                        label={SITUACAO_LABELS[situacao]}
                        size="small"
                        color={SITUACAO_COLORS[situacao]}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    <Chip
                      label={CAT_STATUS_LABELS[cat.status] || cat.status}
                      size="small"
                      color={CAT_STATUS_COLORS[cat.status] || 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                );
              })}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, cats.length)} de {cats.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
